/**
 * useConversationKanbanOptions
 *
 * Retorna categorias e fases ativas do Kanban de Conversas do tenant, já
 * agrupadas por categoria. Usado pelos seletores inline da sidebar do chat
 * (InlineCategoryPicker / InlinePhasePicker) para permitir mover uma conversa
 * entre fases/categorias sem sair da tela de chat.
 *
 * Fonte de dados: RPC server-side `get_kanban_conversation_setup`
 * (mesma usada por `useKanban`). Cache TanStack compartilhado evita refetch.
 *
 * Versão: 1.0.0
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { KanbanCategory, KanbanPhase } from '@/hooks/useKanban';

// Logger condicional do módulo
const log = createLogger('useConversationKanbanOptions');

/** Categoria com a lista de fases associadas (ordenadas por position) */
export interface CategoryWithPhases extends KanbanCategory {
  phases: KanbanPhase[];
}

/** Retorno do hook */
export interface ConversationKanbanOptions {
  categories: KanbanCategory[];
  phases: KanbanPhase[];
  /** Categorias enriquecidas com suas fases (para popovers) */
  categoriesWithPhases: CategoryWithPhases[];
  /** Lookup rápido de fase por id */
  phasesById: Map<string, KanbanPhase>;
  /** Lookup rápido de categoria por id */
  categoriesById: Map<string, KanbanCategory>;
}

/**
 * Busca categorias + fases ativas do tenant via RPC unificada.
 * Cache de 5 minutos — estes dados mudam raramente.
 */
export function useConversationKanbanOptions(enabled: boolean = true) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['conversation-kanban-options', currentTenant?.id],
    queryFn: async (): Promise<ConversationKanbanOptions> => {
      log.debug('Buscando setup do Kanban de Conversas', { tenantId: currentTenant?.id });

      const { data, error } = await (supabase.rpc as any)(
        'get_kanban_conversation_setup',
        { p_tenant_hint: currentTenant!.id },
      );

      if (error) {
        log.error('Erro ao carregar opções do kanban', { error });
        throw error;
      }

      const payload = (data || {}) as {
        categories?: KanbanCategory[];
        phases?: KanbanPhase[];
      };

      // Ordena categorias e fases por position (ordem do kanban)
      const categories = [...(payload.categories || [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      );
      const phases = [...(payload.phases || [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      );

      // Agrupa fases por categoria preservando ordem
      const categoriesWithPhases: CategoryWithPhases[] = categories.map(cat => ({
        ...cat,
        phases: phases.filter(p => p.category_id === cat.id),
      }));

      const phasesById = new Map(phases.map(p => [p.id, p]));
      const categoriesById = new Map(categories.map(c => [c.id, c]));

      return {
        categories,
        phases,
        categoriesWithPhases,
        phasesById,
        categoriesById,
      };
    },
    enabled: enabled && !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 min — config muda raramente
    gcTime: 10 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
